<template>
  <div class="u-avatar" :style="{ fontSize: `${size}px` }">
    <!-- Avatar picture -->
    <picture :data-initials="initials">
      <source v-if="!!src" :srcset="src" />
      <img
        :alt="imgAlt"
        :src="gravatarSrc"
        :class="[!!src && isNativeSrc && 'has-background-white']"
        @load="handleLoadEvent"
      />
    </picture>
    <!-- Bottom left slot -->
    <template v-if="$_.has($slots, 'bottom-left')">
      <div class="avatar-slot-bottom-left">
        <slot name="bottom-left" />
      </div>
    </template>
    <!-- Bottom right slot -->
    <template v-if="$_.has($slots, 'bottom-right')">
      <div class="avatar-slot-bottom-right">
        <slot name="bottom-right" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MD5 from "crypto-js/md5";

export default defineComponent({
  name: "Avatar",
  props: {
    src: { type: String, default: null },
    name: { type: String, default: null },
    email: { type: String, default: "" },
    size: { type: [Number, String], default: 100 }
  },
  data: () => ({
    imgAlt: ""
  }),
  computed: {
    initials(): string {
      const name = (this.name || "")
        .replace(/[^A-Z\s]/gi, "")
        .trim()
        .split(" ");
      if (name.length > 1) {
        const first = this.$_.first(this.$_.first(name)) || "";
        const last = this.$_.first(this.$_.last(name)) || "";
        return `${first + last}`.toUpperCase();
      }
      return name[0].substring(0, 2).toUpperCase();
    },
    gravatarSrc(): string {
      const hash = MD5(this.email).toString();
      return `https://gravatar.com/avatar/${hash}?d=blank&s=200`;
    },
    isNativeSrc(): boolean {
      return !this.src.includes("gravatar.com");
    }
  },
  methods: {
    handleLoadEvent() {
      this.imgAlt = this.name;
    }
  }
});
</script>

<style lang="scss" scoped>
.u-avatar {
  position: relative;

  [class*="avatar-slot-bottom"] {
    position: absolute;
    display: flex;
    bottom: 0;
    z-index: 10;
  }
  .avatar-slot-bottom-left {
    left: 0;
  }
  .avatar-slot-bottom-right {
    right: 0;
  }

  picture {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
    width: 1em;
    height: 1em;
    border-radius: 50%;
    border: 1px solid $white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.25);
    background: $white;
    user-select: none;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
      z-index: 1;
    }

    &[data-initials] {
      // Shared
      &::before {
        position: absolute;
        color: $dark-invert;
        font-size: 0.4em;
        line-height: 1;
        z-index: 1;
      }

      &::after {
        content: "";
        display: block;
        top: 1px;
        bottom: 1px;
        left: 1px;
        right: 1px;
        position: absolute;
        background: $dark;
        z-index: 0;
        border-radius: 50%;
      }

      // Initials
      &:not([data-initials=""])::before {
        content: attr(data-initials);
        font-weight: 500;
      }
      // Icon
      &[data-initials=""]::before {
        content: "\f007";
        font-family: $family-icon;
        font-weight: 900;
        font-style: normal;
        font-variant: normal;
        text-rendering: auto;
        -webkit-font-smoothing: antialiased;
      }
    }
  }
}
</style>
